/**
 * Utility functions to validate cart limits based on product metafields
 */

const CartLimitValidator = {
  /**
   * Checks if adding a quantity of a variant would exceed its max cart limit
   * @param {number} variantId - The variant ID to check
   * @param {number} quantityToAdd - The quantity to add
   * @param {object} cart - The current cart object
   * @param {number|null} maxCartLimit - The maximum cart limit (if known)
   * @returns {object} - { valid: boolean, maxLimit: number, currentQuantity: number }
   */
  async validateCartLimit(variantId, quantityToAdd, cart = null, maxCartLimit = null) {
    // If no max limit is provided, try to get it from the DOM
    if (maxCartLimit === null) {
      const maxCartLimitEl = document.querySelector('[data-product-max-cart-limit]');
      maxCartLimit = maxCartLimitEl ? parseInt(maxCartLimitEl.dataset.productMaxCartLimit, 10) : null;
    }
    
    // If no max limit is set, the validation passes
    if (maxCartLimit === null) {
      return { valid: true, maxLimit: null, currentQuantity: 0 };
    }
    
    // Get the current cart if not provided
    if (!cart) {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) throw new Error(response.status);
        cart = await response.json();
      } catch (error) {
        console.error('Error fetching cart:', error);
        // If we can't fetch the cart, we'll assume the validation passes
        return { valid: true, maxLimit: maxCartLimit, currentQuantity: 0 };
      }
    }
    
    // Find the current quantity of this variant in the cart
    const existingItem = cart.items.find(item => item.variant_id === variantId);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    
    // Check if adding the new quantity would exceed the limit
    const valid = (currentQuantity + quantityToAdd) <= maxCartLimit;
    
    return { 
      valid, 
      maxLimit: maxCartLimit, 
      currentQuantity 
    };
  },
  
  /**
   * Gets the error message for when a cart limit is exceeded
   * @param {number} maxLimit - The maximum cart limit
   * @returns {string} - The error message
   */
  getErrorMessage(maxLimit) {
    return `You can only add up to ${maxLimit} of this product to your cart.`;
  }
};

// Make the validator available globally
window.CartLimitValidator = CartLimitValidator;
